import {
  Create,
  Drawer,
  DrawerProps,
  Edit,
  FormProps,
  Grid
} from '@pankod/refine-antd'
import { useNotification } from '@pankod/refine-core'
import { definitions } from 'interfaces'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { checkIssueDeadline, ISSUE_SUB_TYPE_OTHER_ID, ISSUE_TYPE_REFUND_MADRID_ID, ORDER_STATUS_ID_PENDING_PAYMENT, RETURN_ISSUE_METHOD_DELIVERY, sendEmailRequest, SENT_SUPPLIER_ID, supabaseClient } from 'utility'
import { IssueForm } from './form'

type IssueProps = {
  drawerProps: DrawerProps
  formProps: FormProps
  close: () => void
  refetchFn?: () => void
  isCreate?: boolean
  issueTypeId?: number
  orderId?: number
}

export const IssueDrawer: React.FC<IssueProps> = ({
  drawerProps,
  formProps,
  close,
  refetchFn,
  isCreate = true,
  issueTypeId,
  orderId
}) => {
  const breakpoint = Grid.useBreakpoint()
  const { t } = useTranslation()
  const { open } = useNotification()
  const [itemIds, setItemIds] = useState<number[]>()
  const [loading, setLoading] = useState<boolean>(false)
  const [userGmail, setUserGmail] = useState<string>()
  const [note, setNote] = useState<string>()
  const [canSave, setCanSave] = useState(true)
  const Action = isCreate ? Create : Edit
  const langAction = isCreate ? 'create' : 'edit'
  const breakPointSM = isCreate ? '400px' : '100vh'

  const successNotification = () => open?.({
    type: 'success',
    message: t('notifications.successMsg')
  })

  const errorNotification = () => open?.({
    type: 'error',
    message: t('notifications.errorMsg')
  })

  useEffect(() => {
    if (!isCreate && orderId) {
      const itemsIds = formProps.initialValues?.items
      if (itemsIds) {
        supabaseClient.from('items')
          .select('id')
          .eq('order_id', orderId)
          .in('id', itemsIds)
          .then(data => {
            if (data && data.data && data.data.length > 0) {
              setItemIds(data.data?.map(item => item.id) || [])
            }
          })
      }
    }
  }, [])

  // if (!isCreate && orderId && !itemIds) return null

  return (
    <Drawer
      {...drawerProps}
      width={breakpoint.sm ? breakPointSM : '100%'}
      bodyStyle={{ padding: 0 }}
      zIndex={1001}
    >
      <Action
        isLoading={loading}
        saveButtonProps={{
          disabled: !isCreate && !canSave,
          onClick: async (e: any) => {
            const _issueTypeId = formProps.form?.getFieldValue('issue_type_id')
            const selectedItemIds: number[] = formProps.form?.getFieldValue('item_ids') || []
            const issueSubTypeId = formProps.form?.getFieldValue('issue_sub_type_id')
            const returnMethod = formProps.form?.getFieldValue('return_method')
            const pickupSchedule = formProps.form?.getFieldValue('pickup_schedule') || formProps.initialValues?.home_pickup_data?.schedule
            const pickupDate = formProps.form?.getFieldValue('pickup_date') || formProps.initialValues?.home_pickup_data?.date

            if (!isCreate && ((Number(_issueTypeId) !== ISSUE_TYPE_REFUND_MADRID_ID && Number(issueSubTypeId) !== ISSUE_SUB_TYPE_OTHER_ID && !selectedItemIds.length) ||
              (returnMethod === RETURN_ISSUE_METHOD_DELIVERY && (!pickupSchedule || !pickupDate)))) {
              return
            }

            setLoading(true)

            if (isCreate) {
              const orderId = formProps.form?.getFieldValue('order_id')
              const { data } = await supabaseClient
                .from('orders')
                .select('status_id')
                .eq('id', orderId)

              if (!data || !data.length) {
                setLoading(false)
                errorNotification()
                return
              }

              let finalLocation

              if (issueTypeId === 1) {
                finalLocation = 4
              } else if (issueTypeId === 2) {
                finalLocation = 1

                const currentDate = new Date().toISOString().split('T')[0]

                await supabaseClient
                  .from('orders')
                  .update({
                    return_date: currentDate,
                    return_reason: formProps.form?.getFieldValue('reason') || null,
                    updated_by: supabaseClient.auth.user()?.email || null
                  })
                  .eq('id', orderId)
                  .is('return_date', null)
              } else if (data[0].status_id === 4) {
                finalLocation = 5
              }

              const issue = {
                issue_type_id: issueTypeId || _issueTypeId,
                order_id: orderId,
                // delivery_deadline: deadline ? dayjs().add(deadline, 'day') : null,
                issue_sub_type_id: formProps.form?.getFieldValue('issue_sub_type_id'),
                created_by: supabaseClient.auth.user()?.email,
                location: finalLocation,
                internal: true,
                user_can_show: formProps.form?.getFieldValue('user_can_show') || false
              }

              const { data: dataIssue, error } = await supabaseClient
                .from('issues')
                .insert(issue)

              if (dataIssue?.[0]?.id) {
                await checkIssueDeadline(orderId, dataIssue?.[0]?.id)
              }

              if (error) {
                setLoading(false)
                errorNotification()
                return
              }
              window.open(`/issues/show/${dataIssue[0].id}`)
            } else {
              const issueId = formProps.initialValues?.id

              let homePickupData = null

              const { data: orderShipAddress } = await supabaseClient.from('orders')
                .select('id, shipping_address_id(id, phone_number, full_address)')
                .eq('id', orderId)
                .single()

              const isoDate = formProps.form?.getFieldValue('pickup_date') || formProps.initialValues?.home_pickup_data?.date || null
              const schedule = formProps.form?.getFieldValue('pickup_schedule') || formProps.initialValues?.home_pickup_data?.schedule || null
              const observations = formProps.form?.getFieldValue('observations') || formProps.initialValues?.home_pickup_data?.observations || null
              if (orderShipAddress && orderShipAddress.shipping_address_id) {
                homePickupData = {
                  date: isoDate ? new Date(isoDate).toISOString().split('T')[0] : null,
                  phone: orderShipAddress.shipping_address_id.phone_number,
                  address: orderShipAddress.shipping_address_id.full_address,
                  schedule: schedule,
                  observations: observations
                }
              }

              const issue: Omit<definitions['issues'], 'id' | 'order_id' | 'created_at' | 'deleted' | 'internal'> = {
                issue_type_id: _issueTypeId,
                return_method: formProps.form?.getFieldValue('return_method') || null,
                issue_sub_type_id: formProps.form?.getFieldValue('issue_sub_type_id') || null,
                client_status_id: formProps.form?.getFieldValue('client_status_id') || null,
                issue_status_id: formProps.form?.getFieldValue('issue_status_id') || null,
                reason: formProps.form?.getFieldValue('reason') || null,
                notes: note,
                notes_marketplace: formProps.form?.getFieldValue('notes_marketplace') || null,
                request_date: formProps.form?.getFieldValue('request_date') || null,
                shipping_method: formProps.form?.getFieldValue('shipping_method') || null,
                shipper: formProps.form?.getFieldValue('shipper') || null,
                tracking_id: formProps.form?.getFieldValue('tracking_id') || null,
                issue_deadline: formProps.form?.getFieldValue('issue_deadline') || null,
                // delivery_deadline: formProps.form?.getFieldValue('delivery_deadline') || null,
                is_opened: formProps.form?.getFieldValue('is_opened'),
                is_completed: formProps.form?.getFieldValue('is_completed'),
                is_wallapop: formProps.form?.getFieldValue('is_wallapop'),
                user_can_show: formProps.form?.getFieldValue('user_can_show') || false,
                location: formProps.form?.getFieldValue('issue_status_id') !== 4 ? parseInt(formProps.form?.getFieldValue('location')) : SENT_SUPPLIER_ID,
                updated_by: supabaseClient.auth.user()?.email,
                created_by: userGmail,
                asigned_to: formProps.form?.getFieldValue('asigned_to') || null,
                home_pickup_data: formProps.form?.getFieldValue('return_method') ? homePickupData as any || null : null,
                items: selectedItemIds.map(i => i.toString())
              }
              const { error } = await supabaseClient
                .from('issues')
                .update(issue)
                .eq('id', issueId)

              if (error) {
                setLoading(false)
                errorNotification()
                return
              }

              const { error: selectError } = await supabaseClient
                .from('items')
                .select('id')
                .eq('order_id', orderId)

              if (selectError) {
                setLoading(false)
                errorNotification()
                return
              }

              if (formProps.form?.getFieldValue('return_method') === RETURN_ISSUE_METHOD_DELIVERY && formProps.form?.getFieldValue('sentSup')) {
                // Check if we have a home_pickup with pending payment supplement for this order
                const { data: existingSupplement } = await supabaseClient
                  .from('supplements')
                  .select('id')
                  .eq('order_id', orderId)
                  .eq('description', 'home_pickup')
                  .eq('status_id', ORDER_STATUS_ID_PENDING_PAYMENT)
                  .eq('deleted', false)
                  .single()

                if (!existingSupplement) {
                  const { data: variableData } = await supabaseClient
                    .from('variables')
                    .select('value')
                    .eq('name', 'HOME_PICKUP_SUPPLEMENT_VALUE')
                    .single()

                  const supplementValue = variableData?.value
                    ? parseFloat(variableData.value)
                    : 0

                  if (supplementValue > 0) {
                    await supabaseClient
                      .from('supplements')
                      .insert({
                        order_id: orderId,
                        supplement: supplementValue,
                        status_id: ORDER_STATUS_ID_PENDING_PAYMENT,
                        description: 'home_pickup',
                        volumetric: false,
                        deleted: false,
                        read_by_user: false,
                        reviewed: false,
                        email_sent_at: null,
                        updated_by: supabaseClient.auth.user()?.email
                      })

                    const { data: issueData } = await supabaseClient
                      .from('issues')
                      .select('*')
                      .eq('id', issueId)
                      .single()

                    if (issueData) {
                      await sendEmailRequest(issueData, 'issues', () => null, () => null)
                    }
                  }
                }
              }

              successNotification()
            }

            setLoading(false)
            close()
            window.location.reload()
          }
        }}
        title={t(`issues.titles.${langAction}`)}
        breadcrumb={false}
        goBack={false}
        headerProps={{ extra: null }}
      >
        <IssueForm
          formProps={formProps}
          isCreate={isCreate}
          issueTypeId={issueTypeId}
          orderId={orderId}
          itemIds={itemIds}
          setUserGmail={setUserGmail}
          setNote={setNote}
          setCanSave={setCanSave}
        />
      </Action>
    </Drawer>
  )
}
